import { z } from 'zod';
import { JA_JP } from '@/components/organisms/RA2NDatePicker/constants';
import { parseDate, parseFormat } from '@/components/organisms/RA2NDatePicker/datepickerLogic';

/** BD 6.1 — insurer_number must be present AND half-width digits only. */
const HEALTH_INSURER_NUMBER_REQUIRED_NUMERIC =
  '保険者番号は必須です。半角数字で入力してください。';

export const patientCertificationSchema = z
  .object({
    // 保険者情報
    insurer_number: z
      .string({
        required_error: HEALTH_INSURER_NUMBER_REQUIRED_NUMERIC,
        invalid_type_error: HEALTH_INSURER_NUMBER_REQUIRED_NUMERIC,
      })
      .min(1, HEALTH_INSURER_NUMBER_REQUIRED_NUMERIC)
      .max(8, '保険者番号は8文字以下にしてください。')
      .regex(/^[0-9]+$/, HEALTH_INSURER_NUMBER_REQUIRED_NUMERIC),

    // 保険種別 (RA2: health_insurance_cards.insurance_type_id)
    insurance_type_id: z.number().optional(),

    patient_cost_rate: z.number().default(0),

    relationship_id: z.number().refine(val => val > 0, {
      message: '続柄は必須です。',
    }),
    sex_id: z.number().refine(val => val > 0, {
      message: '性別は必須です。',
    }),

    date_of_birth: z
      .string({
        required_error: '生年月日は必須です。',
        invalid_type_error: '生年月日は必須です。',
      })
      .min(1, '生年月日は必須です。'),

    age: z.number().optional(),

    // 保険証情報
    card_mark: z.string().max(255, '記号は255文字以下にしてください。').optional(),
    card_number: z.string().optional(),
    card_number2: z.string().optional(),

    qualification_date: z
      .string({
        required_error: '資格取得日は必須です。',
        invalid_type_error: '資格取得日は必須です。',
      })
      .min(1, '資格取得日は必須です。'),

    expiration_date: z.string().optional(),
    insurer_name: z.string().optional(),
    insurer_kind_name: z.string().optional(),
    claim_type_id: z.number().optional(),
    insurer_id: z.number().nullish(),
    cost_rate: z.number().nullish(),
    family_cost_rate: z.number().nullish(),
    patient_cost_id: z.number().nullish(),
    elderly_insurance_certificate_id: z.number().nullish(),
    futan_ritsu: z.string().optional(),

    // 受診者情報
    name: z
      .string({
        required_error: '受診者氏名漢字は必須です。',
        invalid_type_error: '受診者氏名漢字は必須です。',
      })
      .transform(s => s.trim())
      .pipe(
        z
          .string()
          .min(1, '受診者氏名漢字は必須です。')
          .max(64, '受診者氏名漢字は64文字以下にしてください。')
      ),

    name_kana: z
      .string()
      .max(64, '受診者氏名仮名は64文字以下にしてください。')
      .optional(),

    // 被保険者情報
    h_name: z
      .string({
        required_error: '被保険者名は必須です。',
        invalid_type_error: '被保険者名は必須です。',
      })
      .transform(s => s.trim())
      .pipe(
        z
          .string()
          .min(1, '被保険者名は必須です。')
          .max(64, '被保険者名は64文字以下にしてください。')
      ),

    h_name_kana: z
      .string()
      .max(64, '被保険者氏名仮名は64文字以下にしてください。')
      .optional(),

    // 住所情報
    post: z.string().max(7, '郵便番号は7文字以下にしてください。').optional(),
    telephone: z.string().max(13, '電話番号は13文字以下で入力してください。').optional(),
    address1: z.string().max(100, '住所1は100文字以下にしてください。').optional(),
    address2: z.string().max(100, '住所2は100文字以下にしてください。').optional(),

    // 公費負担医療情報
    medical_aid: z
      .object({
        insurer_type_id: z.number().optional(),
        medical_aid_number: z.string().optional(),
        medical_aid_patient_number: z.string().optional(),
        application_start_date: z.string().optional(),
        application_end_date: z.string().optional(),
        calc_type: z.string().optional(),
        limit_amount: z.string().optional(),
        limit_num_month: z.string().optional(),
        limit_amount_month: z.string().optional(),
        insurer_id: z.string().optional(),
        insurer_cost_rate: z.string().optional(),
        is_partial_aid: z.number().optional(),
        is_nonoutput_rezept: z.number().optional(),
        claim_type_id: z.string().optional(),
        cost_rate: z.string().optional(),
        family_cost_rate: z.string().optional(),
      })
      .optional(),
  })
  .refine(
    data => {
      // 記号・番号のいずれか１つは必須（ただし insurance_type_id === 2 の場合は除外）
      if (data.insurance_type_id === 2) return true;
      const hasMark = !!data.card_mark?.trim();
      const hasNumber = !!data.card_number?.trim();
      return hasMark || hasNumber;
    },
    {
      message: '記号・番号のいずれか１つは入力してください。',
      path: ['card_mark'],
    }
  )
  .refine(
    data => {
      // 被保険者番号 required when insurance_type_id = 2 (後期高齢) — BD 6.1 field 11
      if (data.insurance_type_id !== 2) return true;
      return !!data.card_number2?.trim();
    },
    {
      message: '被保険者番号は必須です。',
      path: ['card_number2'],
    }
  )
  .refine(
    data => {
      if (!data.qualification_date?.trim() || !data.expiration_date?.trim()) return true;
      // RA2NDatePicker stores 和暦 (e.g. 令和6年01月15日); Date() cannot parse that
      const format = parseFormat(JA_JP.format);
      const q = parseDate(data.qualification_date, format);
      const e = parseDate(data.expiration_date, format);
      if (Number.isNaN(q.getTime()) || Number.isNaN(e.getTime())) return true;
      return e.getTime() >= q.getTime();
    },
    {
      message: '有効期限は資格取得日以降の日付を指定してください。',
      path: ['expiration_date'],
    }
  );

export type TPatientCertificationForm = z.infer<typeof patientCertificationSchema>;
